import type { EmbedErrorDetail } from './embed-widget.types'
import { getEstimatedHeight, cacheRenderedHeight } from './dimension-cache'
import { ensureResourcesLoaded, getEmbedGlobal, resolveLanguage } from './resource-loader'

const HOST_STYLES = `
  :host {
    display: block;
  }

  slot {
    display: none;
  }

  :host([ready]) slot {
    display: contents;
  }
`

/**
 * Builds the `/widget/embed` URL, appending `?lang=` when a language is
 * resolved so that `YipController::setLanguage()` picks it up during
 * request init — the same code path the language switcher uses.
 */
function buildEmbedUrl(communityUrl: string): string {
  const lang = resolveLanguage()
  if (!lang) return `${communityUrl}/widget/embed`

  const params = new URLSearchParams()
  params.set('lang', lang)
  return `${communityUrl}/widget/embed?${params.toString()}`
}

/**
 * `<gs-cc-embed-widget>` — Orchestrator for embedding community widgets on external sites.
 *
 * Loads shared resources (Preact runtime, CSS variables), fetches
 * widget HTML from the backend, and injects it into its light DOM. The response
 * contains a `<gs-cc-widget>` which handles shadow DOM projection, design token
 * injection, and Preact hydration independently.
 *
 * Reserves layout space with a transparent placeholder during loading to prevent
 * content layout shifts. Placeholder height is automatically determined: cached
 * from previous renders of the same widget type, or inferred from the widget
 * type name on first load.
 *
 * @fires embed-loading — Fetch started
 * @fires embed-loaded  — Widget HTML injected into DOM
 * @fires widget-ready  — Inner widget connected, CSS loaded, and hydrated
 * @fires embed-error   — Loading or binding failed
 *
 * @attr page       — Page location (e.g. `homepage`, `shared/header`)
 * @attr type       — Widget type (e.g. `banner`, `quick_links`)
 * @attr min-height — Override placeholder height (e.g. `200px`); bypasses auto-sizing
 */
export class EmbedWidget extends HTMLElement {
  private abortController: AbortController | null = null
  private loadScheduled = false

  /**
   * Attributes that trigger a widget re-fetch when changed.
   * Must be lowercase — HTML `setAttribute` lowercases names per spec.
   */
  static get observedAttributes(): string[] {
    return ['page', 'type']
  }

  attributeChangedCallback(_name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return
    if (this.isConnected) {
      this.scheduleLoad()
    }
  }

  connectedCallback(): void {
    this.initShadowDom()
    this.scheduleLoad()
  }

  /**
   * Coalesce the burst of lifecycle callbacks fired during init — `connectedCallback`
   * plus an `attributeChangedCallback` for each of `page` and `type` — into a single
   * `loadWidget()` on the next microtask. Without this, each callback would abort the
   * previous in-flight `/widget/embed` request and start a new one, producing a string
   * of aborted requests before the successful one.
   *
   * The microtask runs the instant the synchronous init settles — before paint, in
   * parallel with resource loading — so the embed request still fires ASAP, just once,
   * with the final `page`/`type`. Layout space is reserved synchronously in
   * `connectedCallback` (`initShadowDom` → `reserveSpace`), so there is no visual delay.
   */
  private scheduleLoad(): void {
    if (this.loadScheduled) return
    this.loadScheduled = true
    queueMicrotask(() => {
      this.loadScheduled = false
      if (this.isConnected) this.loadWidget()
    })
  }

  disconnectedCallback(): void {
    this.abort()
    this.innerHTML = ''
    this.style.minHeight = ''
    this.removeAttribute('loading')
    this.removeAttribute('ready')
  }

  private get page(): string {
    return this.getAttribute('page') ?? ''
  }

  private get widgetType(): string {
    return this.getAttribute('type') ?? ''
  }

  private initShadowDom(): void {
    if (this.shadowRoot) return

    const shadow = this.attachShadow({ mode: 'open' })
    shadow.innerHTML = `<style>${HOST_STYLES}</style><slot></slot>`

    this.reserveSpace()
  }

  /**
   * Reserves layout space on the host element using (in priority order):
   * 1. Explicit `min-height` attribute on the element
   * 2. Cached height from a previous render of this widget type
   * 3. Heuristic default based on the widget type name
   */
  private reserveSpace(): void {
    const explicit = this.getAttribute('min-height')
    if (explicit) {
      this.style.minHeight = explicit
      return
    }

    const { widgetType } = this
    if (!widgetType) return

    this.style.minHeight = `${getEstimatedHeight(widgetType)}px`
  }

  private buildRequestBody(): Record<string, unknown> {
    const global = getEmbedGlobal()
    const body: Record<string, unknown> = {
      page: this.page,
      type: this.widgetType
    }

    body.context = {
      productType: global.productType,
      ...global.context,
      language: resolveLanguage()
    }

    return body
  }

  private abort(): void {
    if (this.abortController) {
      this.abortController.abort()
      this.abortController = null
    }
  }

  private async loadWidget(): Promise<void> {
    const embed = getEmbedGlobal()
    // Requests route through the SJ widget-proxy when set, else the community URL.
    const requestBaseUrl = embed.widgetProxyUrl ?? embed.communityUrl
    const { page, widgetType } = this

    if (!requestBaseUrl) {
      this.emitError('window.__insidedEmbed.communityUrl (or widgetProxyUrl) is required')
      return
    }

    if (!page || !widgetType) {
      return
    }

    this.abort()
    this.abortController = new AbortController()
    const { signal } = this.abortController

    this.removeAttribute('ready')
    this.reserveSpace()
    this.setAttribute('loading', '')
    this.dispatch('embed-loading')

    try {
      // The embed render is anonymous and needs no CSRF token, so fire
      // /widget/embed immediately and in parallel with resources + resourcesBody.
      const resourcesPromise = ensureResourcesLoaded(requestBaseUrl)

      const headers: Record<string, string> = {
        Accept: 'text/html',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }

      const embedUrl = buildEmbedUrl(requestBaseUrl)

      const embedPromise = fetch(embedUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(this.buildRequestBody()),
        signal
      })

      const [, response] = await Promise.all([resourcesPromise, embedPromise])

      if (!response.ok) {
        throw new Error(`Failed to fetch widget: ${response.status}`)
      }

      const html = await response.text()

      if (signal.aborted) return

      this.innerHTML = html
      this.dispatch('embed-loaded')

      this.awaitWidgetReady()
    } catch (error) {
      this.removeAttribute('loading')
      if (signal.aborted) return

      this.handleLoadError(error)
    }
  }

  /**
   * Waits for the inner `<gs-cc-widget>` to signal that its stylesheets are
   * loaded before transitioning from the placeholder to the widget content.
   */
  private awaitWidgetReady(): void {
    const widget = this.querySelector<HTMLElement & { cssReady?: boolean }>('gs-cc-widget')

    if (!widget) {
      this.reveal()
      return
    }

    if (widget.cssReady) {
      this.reveal()
      this.cacheHeight()
      this.dispatch('widget-ready')
      return
    }

    widget.addEventListener(
      'widget-css-ready',
      () => {
        this.reveal()
        this.cacheHeight()
        this.dispatch('widget-ready')
      },
      { once: true }
    )
  }

  /** Transition from placeholder to widget content. */
  private reveal(): void {
    this.removeAttribute('loading')
    this.style.minHeight = ''
    this.setAttribute('ready', '')
  }

  /** Measure and cache the rendered widget height for future placeholder sizing. */
  private cacheHeight(): void {
    const { widgetType } = this
    if (!widgetType || this.getAttribute('min-height')) return

    requestAnimationFrame(() => {
      const height = this.getBoundingClientRect().height
      if (height > 0) {
        cacheRenderedHeight(widgetType, height)
      }
    })
  }

  private handleLoadError(error: unknown): void {
    const message = error instanceof Error ? error.message : String(error)
    const errorObj = error instanceof Error ? error : undefined
    this.emitError(message, errorObj)
  }

  private dispatch(type: string): void {
    this.dispatchEvent(new CustomEvent(type, { bubbles: true, composed: true }))
  }

  private emitError(message: string, error?: Error): void {
    console.error(`[EmbedWidget] ${message}`, error)
    this.dispatchEvent(
      new CustomEvent<EmbedErrorDetail>('embed-error', {
        bubbles: true,
        composed: true,
        detail: { message, error }
      })
    )
  }
}

if (typeof customElements !== 'undefined') {
  if (!customElements.get('gs-cc-embed-widget')) {
    customElements.define('gs-cc-embed-widget', EmbedWidget)
  }
}
